import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Radio,
  VStack,
} from "@chakra-ui/react"
import { useRange } from "@hooks/search/useRange"
import { ChangeEvent, useState } from "react"
import { useNumericMenu } from "react-instantsearch-core"

const priceOptions = (min: number, max: number, avg: number) => {
  const average = Math.round(avg / 10) * 10
  const slab = average / 2

  const options: { label: string; start?: number; end?: number }[] = [
    { label: "Any Price" },
  ]

  if (min && max) {
    if (min < slab && max > slab) {
      options.push({
        label: `Upto AED ${slab}`,
        end: slab,
      })
    }

    let count = 1
    while (count < 3 && min < slab && max > slab) {
      options.push({
        label: `AED ${slab * count} - AED ${slab * (count + 1)}`,
        start: slab * count,
        end: slab * (count + 1),
      })
      count++
    }

    if (min < slab * count && max > slab * count) {
      options.push({
        label: `AED ${slab * count} and above`,
        start: slab * count,
      })
    } else if (max > slab * count) {
      options.push({
        label: `Upto AED ${max}`,
        start: min,
        end: max,
      })
    }
  }

  return options
}

function PricingFilter() {
  const { range, canRefine, refine, start } = useRange({
    attribute: "offers.lowPrice",
  })

  const { items } = useNumericMenu({
    attribute: "offers.lowPrice",
    items: priceOptions(range.min || 0, range.max || 0, range.avg || 0),
  })

  const startMin = Number.isFinite(start[0]) ? start[0] : range.min
  const startMax = Number.isFinite(start[1]) ? start[1] : range.max

  const [min, setMin] = useState<number | string | null | undefined>(
    startMin || 0
  )
  const [max, setMax] = useState<number | string | null | undefined>(
    startMax || 0
  )

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const refined = JSON.parse(decodeURI(event.target.value))
    refine([refined.start, refined.end])
    if (refined.start) {
      setMin(refined.start)
    } else {
      setMin(range.min || 0)
    }
    if (refined.end) {
      setMax(refined.end)
    } else {
      setMax(range.max || 0)
    }
  }

  return (
    <>
      <VStack alignItems={"start"}>
        {items.map((item) => (
          <Radio
            key={item.value}
            isChecked={item.isRefined}
            onChange={handleChange}
            value={item.value}
            colorScheme={"yellow"}
          >
            <Box
              as={"span"}
              fontSize={"sm"}
            >
              {item.label}
            </Box>
          </Radio>
        ))}
      </VStack>

      {canRefine && (
        <HStack
          justifyContent={"flex-start"}
          alignItems={"center"}
          mt={6}
          spacing={0}
        >
          <InputGroup>
            <InputLeftAddon
              fontSize={"xs"}
              ps={1}
              pe={0}
              bgColor={"transparent"}
            >
              {"AED"}
            </InputLeftAddon>
            <Input
              w={"12"}
              px={1}
              textAlign={"center"}
              borderStart={"none"}
              placeholder={`Min`}
              enterKeyHint={"go"}
              onChange={(e) => {
                if (!e.target.value) {
                  setMin(undefined)
                } else {
                  setMin(Number(e.target.value))
                }
              }}
              value={min || ""}
              type={"number"}
              min={range.min}
              max={range.max}
              isInvalid={
                !!min &&
                !!range.min &&
                !!range.max &&
                (Number(min) < range.min || Number(min) > range.max)
              }
            />
          </InputGroup>
          <InputGroup>
            <InputLeftAddon
              fontSize={"xs"}
              ps={1}
              pe={0}
              bgColor={"transparent"}
            >
              {"AED"}
            </InputLeftAddon>
            <Input
              w={"12"}
              px={1}
              textAlign={"center"}
              borderStart={"none"}
              size={"md"}
              placeholder={`Max`}
              enterKeyHint={"go"}
              onChange={(e) => {
                if (!e.target.value) {
                  setMax(undefined)
                } else {
                  setMax(Number(e.target.value))
                }
              }}
              value={max || ""}
              type={"number"}
              min={range.min}
              max={range.max}
              isInvalid={
                !!max &&
                !!range.min &&
                !!range.max &&
                (Number(max) < range.min || Number(max) > range.max)
              }
            />
          </InputGroup>
          <Button
            size={"sm"}
            onClick={() => {
              refine([
                Number.isFinite(Number(min)) ? Number(min) : undefined,
                Number.isFinite(Number(max)) ? Number(max) : undefined,
              ])
            }}
          >
            {"Go"}
          </Button>
        </HStack>
      )}
    </>
  )
}

export default PricingFilter
